// StoryDetail.jsx
import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import { goBack } from 'redux-first-history';

import fetchMessagesByUsingList from '../action/fetchMessagesByUsingList.js';
import { replace } from '../action/navigationAction.js';
import Tooltip from '../component/Tooltip.jsx';
import StoryMessage from '../container/StoryMessage.js';
import useSwipeableFeed from '../hook/useSwipeableFeed.js';
import { EMPTY_ARRAY } from '../resource/defaults.js';
import {
  drmRulesetTestStatus as DrmTestStatus,
  DrmFeatureName,
} from '../resource/drmConstants.js';
import getIsMatchedPathname from '../resource/getIsMatchedPathname.js';
import { LoadMode } from '../resource/playerConfig.js';
import { getIsOnMobile } from '../resource/getUserAgent.js';
import {
  VOD_PLAYER_POOL_SIZE_DESKTOP_MAX,
  VOD_PLAYER_POOL_SIZE_MOBILE_MAX,
} from '../RemoteConfigKeys.js';
import getMessageData from '../selector/getMessageData.js';
import getMessageList from '../selector/getMessageList.js';
import getOperationData from '../selector/getOperationData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getStoryPreviewIds from '../selector/getStoryPreviewIds.js';
import getCurrentEngine, {
  PlayerEngine,
} from '../selector/getCurrentEngine.js';
import { ButtonId } from '../resource/trackingConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import {
  breakpoint,
  color,
  footerNavigationHeight,
} from '../style/variables.js';

import CloseIcon from '../../img/ic-topbar-close.svg';

const StoryDetail = ({ shouldLoop = false }) => {
  const { messageId } = useParams();
  const location = useLocation();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isOnMobile = getIsOnMobile();

  const listPath = location.state?.listPath;
  const paginationPage = location.state?.page;
  const usingList = useMemo(() => listPath || EMPTY_ARRAY, [listPath]);
  const listType = usingList[0];

  const itemIds =
    useSelector(state => getMessageList(state, listType, usingList)) ||
    EMPTY_ARRAY;
  const nextPage = useSelector(state =>
    getMessageList(state, listType, usingList, 'nextPage')
  );
  const storyPreviewIds = useSelector(getStoryPreviewIds) || EMPTY_ARRAY;

  const isOnPostDetail = getIsMatchedPathname({
    pathname: location?.pathname,
    dataKey: 'isOnPostDetail',
  });

  const fetchMore = useCallback(() => {
    const page = nextPage || (paginationPage ? paginationPage + 1 : null);
    if (page) {
      dispatch(
        fetchMessagesByUsingList({
          usingList,
          page,
        })
      );
    }
  }, [dispatch, usingList, nextPage, paginationPage]);

  const feedData = useMemo(() => {
    if (itemIds.length === 0 || !itemIds.includes(messageId)) {
      return [{ id: messageId }];
    }
    return itemIds.map(id => ({ id }));
  }, [itemIds, messageId]);

  const isCurrentMessageInList =
    itemIds.length > 0 && itemIds.includes(messageId);

  useEffect(() => {
    if (!isCurrentMessageInList) {
      fetchMore();
    }
  }, [isCurrentMessageInList, fetchMore]);

  const hasUnlockedVideo = useSelector(state => {
    return feedData.some(
      video => getMessageData(state, video.id, 'canViewMedia') === true
    );
  });

  const drmTestStatus = useSelector(state =>
    getOperationData(state, ['drm'], 'testStatus')
  );
  const shouldCheckDrm =
    hasUnlockedVideo &&
    drmTestStatus !== DrmTestStatus.TESTED &&
    drmTestStatus !== DrmTestStatus.TESTING;

  useEffect(() => {
    if (!shouldCheckDrm) return;
    const runDrmCheck = async () => {
      const checkDrmCapabilityBeforePlay = (
        await import('../action/checkDrmCapabilityBeforePlay.js')
      ).default;
      dispatch(
        checkDrmCapabilityBeforePlay({
          drmFeatureName: DrmFeatureName.STORY,
        })
      );
    };
    runDrmCheck();
  }, [dispatch, shouldCheckDrm]);

  const currentEngine = useSelector(getCurrentEngine);

  const remotePoolSize = useSelector(state =>
    getRemoteConfigData(
      state,
      isOnMobile
        ? VOD_PLAYER_POOL_SIZE_MOBILE_MAX
        : VOD_PLAYER_POOL_SIZE_DESKTOP_MAX
    )
  );
  const poolSize =
    remotePoolSize ||
    (isOnMobile
      ? Number(process.env.VOD_PLAYER_POOL_SIZE_MOBILE_MAX)
      : Number(process.env.VOD_PLAYER_POOL_SIZE_DESKTOP_MAX));

  const handleActiveChange = useCallback(
    (newMessageId, index) => {
      if (newMessageId === messageId) return;

      if (index + 2 >= itemIds.length) {
        fetchMore();
      }

      const baseRoute = isOnPostDetail
        ? 'post'
        : storyPreviewIds.includes(newMessageId)
          ? 'story-preview'
          : location.pathname.startsWith('/message/')
            ? 'message'
            : 'story';
      dispatch(
        replace(`/${baseRoute}/${newMessageId}${location.search}`, {
          listPath: usingList,
        })
      );
    },
    [
      messageId,
      itemIds.length,
      fetchMore,
      storyPreviewIds,
      isOnPostDetail,
      location.search,
      location.pathname,
      dispatch,
      usingList,
    ]
  );

  const { getItemState, goToNext, goToPrev } = useSwipeableFeed({
    items: feedData,
    windowSize: poolSize,
    initialActiveId: messageId,
    onActiveChange: handleActiveChange,
  });

  const handleCloseClick = useCallback(() => dispatch(goBack()), [dispatch]);

  return (
    <>
      <StyledWrapper>
        <StyledVideoFeed>
          {currentEngine === PlayerEngine.PRESTOPLAY ? (
            feedData.map((video, index) => {
              const { isActive, isPreloading, slotIndex } = getItemState(index);

              // Only render items within the preload window
              if (slotIndex === -1) return null;

              const loadMode = isActive
                ? LoadMode.ACTIVE
                : isPreloading
                  ? LoadMode.PRELOAD
                  : LoadMode.NONE;

              return (
                <StoryMessage
                  key={video.id}
                  messageId={video.id}
                  isActive={isActive}
                  isLastItem={index === feedData.length - 1}
                  loadMode={loadMode}
                  shouldLoop={shouldLoop}
                  onGoToNext={goToNext}
                  onGoToPrev={goToPrev}
                />
              );
            })
          ) : (
            <StoryMessage
              key={messageId}
              messageId={messageId}
              isActive
              isLastItem={
                feedData.findIndex(video => video.id === messageId) ===
                feedData.length - 1
              }
              loadMode={LoadMode.ACTIVE}
              shouldLoop={shouldLoop}
              onGoToNext={goToNext}
              onGoToPrev={goToPrev}
            />
          )}
        </StyledVideoFeed>
      </StyledWrapper>

      <CloseButtonWrapper>
        <Tooltip
          position="top"
          renderHint={() => (
            <TooltipHint>
              {t('general_close', { ns: TranslationNamespace.GENERAL })}
            </TooltipHint>
          )}
          trigger="hover"
        >
          <CloseButton
            data-element_id={ButtonId.All.ButtonClose}
            onClick={handleCloseClick}
          >
            <img src={CloseIcon} width={24} height={24} alt="close" />
          </CloseButton>
        </Tooltip>
      </CloseButtonWrapper>
    </>
  );
};

StoryDetail.propTypes = {
  shouldLoop: PropTypes.bool,
};

const StyledWrapper = styled.div`
  position: fixed;
  top: var(--space-to-pinned-notification, 0px);
  left: 0;
  bottom: ${footerNavigationHeight}px;
  right: 0;
  width: 100%;
  height: calc(
    100% - var(--space-to-pinned-notification, 0px) -
      ${footerNavigationHeight}px
  );

  @media (min-width: ${breakpoint.mobile}px) {
    width: auto;
    height: calc(100% - 104px - var(--space-to-pinned-notification, 0px));
    aspect-ratio: 9 / 16;
    top: calc(84px + var(--space-to-pinned-notification, 0px));
    bottom: 20px;
    left: 50%;
    right: auto;
    transform: translate(-50%, 0%);
    max-width: calc(100% - 152px);
  }
`;

const StyledVideoFeed = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background: ${color.black};
`;

const CloseButtonWrapper = styled.div`
  display: none;
  @media (min-width: ${breakpoint.mobile}px) {
    width: 32px;
    height: 32px;
    position: fixed;
    top: calc(84px + var(--space-to-pinned-notification));
    right: 20px;
    display: block;
    z-index: 10;
  }
`;

const CloseButton = styled.button`
  ${ResetButtonStyle}
  width: 32px;
  height: 32px;
  border-radius: 50%;
  position: fixed;
  top: calc(84px + var(--space-to-pinned-notification));
  right: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${color.neutral['0_alpha_20']};
  @media (hover: hover) {
    :hover {
      background-color: ${color.neutral['55_alpha_20']};
    }
  }
`;

const TooltipHint = styled.div`
  min-width: 44px;
  height: 30px;
  padding: 8px;
  border-radius: 8px;
  box-shadow: 0 2px 8px 0 ${color.neutral['0_alpha_50']};
  background-color: ${color.neutral['100_alpha_80']};
  color: ${color.black};
  text-align: center;
  white-space: nowrap;
  font-size: 14px;
  margin-left: -22px;
`;

export default StoryDetail;
